import { FC, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import { DeleteIcon } from '../../../team/components/CreateMember/DeleteIcon';
import { TStep } from '../../../../types';

interface StepInputProps {
  steps: TStep[];
  onChange: (steps: TStep[]) => void;
  maxSteps?: number;
  currencyMark?: string;
}

const StepInput: FC<StepInputProps> = ({
  steps, onChange, maxSteps = 5, currencyMark = '$',
}) => {
  const { t } = useTranslation('commissions');
  const [localSteps, setLocalSteps] = useState(steps);
  const [errorStates, setErrorStates] = useState<boolean[]>(new Array(steps.length).fill(false));

  const handleStepChange = (index: number, key: keyof TStep, value: number | string | boolean) => {
    const updatedSteps = localSteps.map((step, idx) => {
      if (idx !== index) return step;

      const newErrorStates = [...errorStates];
      if (key === 'to' && typeof value === 'number' && value <= Number(step.from)) {
        newErrorStates[index] = true; // Set error state for this step
        setErrorStates(newErrorStates);
        return step;
      }
      newErrorStates[index] = false; // Clear error state if valid
      setErrorStates(newErrorStates);

      return { ...step, [key]: value };
    });

    // Adjust subsequent steps' `from` values to be `to + 1`
    if (key === 'to' && typeof value === 'number' && index < localSteps.length - 1) {
      updatedSteps[index + 1] = {
        ...updatedSteps[index + 1],
        from: value + 1,
      };
    }

    setLocalSteps(updatedSteps);
    onChange(updatedSteps);
  };

  const addStep = () => {
    if (localSteps.length < maxSteps) {
      const lastStep = localSteps[localSteps.length - 1];
      const newStep: TStep = {
        index: localSteps.length,
        from: Number(lastStep.to) + 1,
        to: 0,
        amount: 0,
        percent: 0,
      };
      const updatedSteps = [...localSteps, newStep];
      setLocalSteps(updatedSteps);
      onChange(updatedSteps);

      // Update the error states array
      setErrorStates([...errorStates, false]);
    }
  };

  const deleteStep = (index: number) => {
    const updatedSteps = localSteps.filter((_, idx) => idx !== index);

    // Reindex remaining steps and adjust `from` values
    const reindexedSteps = updatedSteps.map((step, idx) => {
      const updatedStep = { ...step };
      if (idx === 0 && updatedSteps.length > 0) {
        updatedStep.from = Number(localSteps[0].from);
      } else if (idx > 0) {
        updatedStep.from = Number(updatedSteps[idx - 1].to) + 1;
      }
      return { ...updatedStep, index: idx };
    });

    setLocalSteps(reindexedSteps);
    onChange(reindexedSteps);

    // Update the error states array
    const updatedErrorStates = errorStates.filter((_, idx) => idx !== index);
    setErrorStates(updatedErrorStates);
  };

  const handleTypeChange = (index: number, value: string) => {
    const isPercentage = value === '1';

    // Reset percent and amount on type change
    const updatedSteps = localSteps.map((step, idx) => {
      if (idx === index) {
        return {
          ...step,
          isPercentage,
          percent: 0, // Reset percent
          amount: 0, // Reset amount
        };
      }
      return step;
    });

    setLocalSteps(updatedSteps);
    onChange(updatedSteps);
  };

  return (
    <Grid container spacing={2}>
      {localSteps.map((step, index) => (
        <Grid container key={index?.toString()} spacing={2} alignItems="center">
          <Grid size={{ xs: 6, sm: 2, md: 2 }}>
            <NumericFormat
              customInput={TextField}
              size="small"
              label={t('from')}
              value={step.from}
              onValueChange={(values) => handleStepChange(index, 'from', Number(values.value))}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 2, md: 2 }}>
            <NumericFormat
              customInput={TextField}
              size="small"
              label={t('to')}
              value={step.to}
              onValueChange={(values) => handleStepChange(index, 'to', Number(values.value))}
              fullWidth
              error={errorStates[index]} // Apply error state
            />
          </Grid>
          <Grid display="flex" justifyContent="center" size={{ xs: 6, md: 4, sm: 4 }}>
            <RadioGroup
              row
              value={step.isPercentage ? '1' : '0'} // Use strings '1' for percentage and '0' for flat
              onChange={(e) => handleTypeChange(index, e.target.value)}
            >
              <FormControlLabel value="0" control={<Radio />} label={t('flat')} />
              <FormControlLabel value="1" control={<Radio />} label={t('percentage')} />
            </RadioGroup>
          </Grid>
          <Grid size={{ xs: 5, sm: 2, md: 3 }}>
            <NumericFormat
              customInput={TextField}
              label={t('compensation')}
              size="small"
              value={step.isPercentage ? step.percent : step.amount}
              onValueChange={(values) => handleStepChange(
                index,
                step.isPercentage ? 'percent' : 'amount',
                Number(values.value),
              )}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2">
                      {step.isPercentage ? '%' : currencyMark}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 1, md: 1 }}>
            {localSteps.length > 1 && index > 0 && (
              <DeleteIcon size={0.8} onClick={() => deleteStep(index)} />
            )}
          </Grid>
          <Grid>
            {errorStates[index] && (
              <Box pt={4} pb={12}>
                <Alert variant="filled" sx={{ bgcolor: '#FFC107', color: '#161C24' }} severity="info">
                  {errorStates[index] ? t('fieldErrorMsg') : ''}
                </Alert>
              </Box>
            )}
          </Grid>
        </Grid>
      ))}
      {localSteps.length < maxSteps && (
        <Box mt={2}>
          <Button variant="outlined" onClick={addStep}>
            {t('addStep')}
          </Button>
        </Box>
      )}
    </Grid>
  );
};

export { StepInput };
